import { Clock3, Coins, CreditCard, ReceiptText, ShieldCheck, WalletCards } from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { Badge, formatDate } from "../components/Badge";
import { Field, Input, Select } from "../components/Form";
import { Empty, ErrorState, Failure, Loading, Pagination, Success } from "../components/State";
import { api, idempotencyKey, json, normalizePage, queryString } from "../lib/api";
import type { Page } from "../lib/types";
import { useAsync } from "../lib/useAsync";

type Currency = "CNY" | "USD" | string;
type Wallet = {
  balance_cents?: number;
  available_cents?: number;
  frozen_cents?: number;
  month_spend_cents?: number;
  pending_reward_cents?: number;
  currency?: Currency;
  updated_at?: string;
};
type WalletEntry = {
  id: string;
  type?: string;
  direction?: "credit" | "debit" | string;
  amount_cents?: number;
  amount?: number;
  currency?: Currency;
  status?: string;
  product_name?: string;
  app_name?: string;
  model_name?: string;
  gateway_request_id?: string;
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  description?: string;
  created_at: string;
};
type Recharge = {
  id: string;
  amount_cents?: number;
  amount?: number;
  currency?: Currency;
  status: string;
  channel?: string;
  checkout_url?: string;
  paid_at?: string;
  created_at: string;
};
type RechargeResult = Recharge & { balance_cents?: number; available_cents?: number };

const rechargePlans = [
  { label: "体验包", amount_cents: 5000 },
  { label: "团队包", amount_cents: 20000 },
  { label: "企业包", amount_cents: 100000 },
];

export function WalletPage() {
  const wallet = useAsync(() => api<Wallet>("/wallet"), []);
  const [entryPage, setEntryPage] = useState(1);
  const entries = useAsync(async () => normalizePage(await api<Page<WalletEntry>>(`/wallet/entries${queryString({ page: entryPage, pageSize: 10 })}`)), [entryPage]);
  const recharges = useAsync(async () => normalizePage(await api<Page<Recharge>>("/wallet/recharges?page=1&pageSize=5")), []);
  const [selectedPlan, setSelectedPlan] = useState(rechargePlans[1].amount_cents);
  const [customAmount, setCustomAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const currency = wallet.data?.currency || "CNY";
  const customAmountValid = customAmount === "" || /^\d+(?:\.\d{1,2})?$/.test(customAmount);
  const selectedAmount = useMemo(() => {
    if (!customAmount) return selectedPlan;
    if (!customAmountValid) return 0;
    return Math.round(Number(customAmount) * 100);
  }, [customAmount, customAmountValid, selectedPlan]);

  const recharge = async (event: FormEvent) => {
    event.preventDefault();
    if (!customAmountValid) {
      setError("请输入有效金额，最多保留两位小数");
      return;
    }
    if (selectedAmount < 100) {
      setError("充值金额不能低于 1 元");
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const result = await api<RechargeResult>("/wallet/recharges", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey() },
        body: json({ amountCents: selectedAmount, currency }),
      });
      setMessage(["paid", "succeeded", "settled"].includes(result.status) ? "充值已到账，平台余额已更新" : "充值单已创建，支付确认后自动到账");
      await Promise.all([wallet.retry(), recharges.retry(), entries.retry()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "创建充值单失败");
    } finally {
      setSubmitting(false);
    }
  };

  if (wallet.loading) return <Loading label="正在读取平台余额" />;
  if (wallet.error || !wallet.data) return <ErrorState error={wallet.error || new Error("余额账户不存在")} retry={wallet.retry} />;

  return <div className="account-page page-shell wallet-page">
    <header className="page-heading split"><div><span>PLATFORM BALANCE</span><h1>平台余额</h1><p>余额只能用于平台内 AI 应用消费，不支持交易、转账或提现。</p></div><button className="secondary-button" onClick={() => void Promise.all([wallet.retry(), entries.retry(), recharges.retry()])}><ReceiptText size={16} />刷新明细</button></header>
    {message && <Success>{message}</Success>}
    {error && <Failure>{error}</Failure>}
    <section className="metric-grid platform-metrics wallet-metrics">
      <article className="accent"><span><WalletCards size={20} /></span><div><small>可用余额</small><strong>{formatMoney(wallet.data.available_cents ?? wallet.data.balance_cents ?? 0, currency)}</strong></div></article>
      <article><span><ShieldCheck size={20} /></span><div><small>执行中冻结</small><strong>{formatMoney(wallet.data.frozen_cents ?? 0, currency)}</strong></div></article>
      <article><span><Coins size={20} /></span><div><small>本月 AI 消费</small><strong>{formatMoney(wallet.data.month_spend_cents ?? 0, currency)}</strong></div></article>
      <article><span><Clock3 size={20} /></span><div><small>待到账奖励</small><strong>{formatMoney(wallet.data.pending_reward_cents ?? 0, currency)}</strong></div></article>
    </section>

    <div className="wallet-overview-grid">
      <section className="admin-panel platform-panel wallet-recharge-panel">
        <header><div><span>BALANCE RECHARGE</span><h2>购买平台余额</h2><p>不同模型按网关计价扣减余额，前台只展示固定金额。</p></div></header>
        <form className="wallet-recharge-form" onSubmit={recharge}>
          <div className="wallet-recharge-fields">
            <Field label="充值套餐" required><Select value={selectedPlan} onChange={(event) => { setSelectedPlan(Number(event.target.value)); setCustomAmount(""); }}>{rechargePlans.map((plan) => <option key={plan.amount_cents} value={plan.amount_cents}>{plan.label} · {formatMoney(plan.amount_cents, currency)}</option>)}</Select></Field>
            <Field label="自定义金额" hint={customAmountValid ? "填写后优先使用自定义金额" : "请输入有效金额，最多保留两位小数"}><Input inputMode="decimal" aria-invalid={!customAmountValid} value={customAmount} onChange={(event) => { setCustomAmount(event.target.value.replace(/[^\d.]/g, "")); setError(""); }} placeholder="例如 300.00" /></Field>
          </div>
          <div className="wallet-purchase-total"><div><span>本次购买</span><small>支付确认后自动计入可用余额</small></div><strong className={customAmountValid ? "" : "invalid"} aria-live="polite">{customAmountValid ? formatMoney(selectedAmount, currency) : "金额格式有误"}</strong></div>
          <button className="primary-button wallet-purchase-button" disabled={submitting || !customAmountValid}>{submitting ? "正在创建充值单" : "确认购买"}<CreditCard size={16} /></button>
        </form>
      </section>

      <section className="admin-panel platform-panel wallet-rules-panel">
        <header><div><span>RULES</span><h2>余额规则</h2><p>资金只在购买余额时进入结算，余额本身不作为可交易资产。</p></div></header>
        <div className="platform-rule-list wallet-rule-list">
          <article><span className="rule-icon"><Coins size={17} /></span><div><strong>统一支付 AI 服务</strong><p>应用和模型网关产生的费用统一从平台余额扣减，明细可追溯。</p></div><Badge tone="success">已启用</Badge></article>
          <article><span className="rule-icon"><ShieldCheck size={17} /></span><div><strong>不可交易与提现</strong><p>余额不能在用户之间转账、交易，也不能兑换或提取现金。</p></div><Badge>固定规则</Badge></article>
          <article><span className="rule-icon"><Clock3 size={17} /></span><div><strong>邀请奖励直接入账</strong><p>仅在被邀请人成功购买余额后触发，可继续用于平台消费。</p></div><Badge tone="accent">邀请奖励</Badge></article>
        </div>
      </section>
    </div>

    <section className="detail-section account-detail-section wallet-ledger-section">
      <div className="account-section-heading"><span>USAGE LEDGER</span><h2>余额明细</h2><p>产品、应用、模型和网关计量用于核对扣费，不改变余额展示口径。</p></div>
      {entries.loading ? <Loading label="正在读取余额明细" /> : entries.error ? <ErrorState error={entries.error} retry={entries.retry} /> : !entries.data?.items.length ? <Empty title="暂无余额明细" detail="充值、AI 消费和邀请奖励都会记录在这里。" /> : <><div className="table-wrap"><table><thead><tr><th>时间</th><th>类型</th><th>金额</th><th>产品 / 应用</th><th>模型计量</th><th>状态</th></tr></thead><tbody>{entries.data.items.map((entry) => <tr key={entry.id}><td>{formatDate(entry.created_at)}</td><td><strong>{entryTypeLabel(entry)}</strong><small>{entry.description || entry.gateway_request_id || "-"}</small></td><td><strong>{entry.direction === "debit" ? "-" : "+"}{formatMoney(readAmount(entry), entry.currency || currency)}</strong></td><td><strong>{entry.product_name || "平台余额"}</strong><small>{entry.app_name || "当前账号"}</small></td><td><strong>{entry.model_name || "-"}</strong><small>{formatTokens(entry)}</small></td><td>{entryStatus(entry.status, entry.direction)}</td></tr>)}</tbody></table></div><Pagination page={entryPage} pageSize={entries.data.page_size || 10} total={entries.data.total || 0} onChange={setEntryPage} /></>}
    </section>

    <section className="detail-section account-detail-section wallet-recharge-history">
      <div className="account-section-heading"><span>RECHARGE ORDERS</span><h2>最近充值</h2><p>支付确认后自动写入余额，无需手动登记凭证。</p></div>
      {recharges.loading ? <Loading label="正在读取充值记录" /> : recharges.error ? <ErrorState error={recharges.error} retry={recharges.retry} /> : !recharges.data?.items.length ? <Empty title="暂无充值记录" detail="创建第一笔充值后会显示在这里。" /> : <div className="report-list">{recharges.data.items.map((item) => <article className={`recharge-record ${rechargeTone(item.status)}`} key={item.id}><span><CreditCard size={18} /></span><div><div><Badge tone={rechargeTone(item.status)}>{rechargeStatus(item.status)}</Badge><small>{item.channel || "平台支付"}</small></div><strong>{formatMoney(readAmount(item), item.currency || currency)}</strong><p>创建于 {formatDate(item.created_at)}{item.paid_at ? ` · 到账 ${formatDate(item.paid_at)}` : ""}</p></div>{item.checkout_url && !["paid", "succeeded", "settled"].includes(item.status) && <a className="primary-button" href={item.checkout_url}>继续支付</a>}</article>)}</div>}
    </section>
  </div>;
}

function readAmount(value: { amount_cents?: number; amount?: number }) {
  if (typeof value.amount_cents === "number") return value.amount_cents;
  if (typeof value.amount === "number") return Math.round(value.amount * 100);
  return 0;
}

function formatMoney(cents: number, currency: Currency) {
  return new Intl.NumberFormat(currency === "USD" ? "en-US" : "zh-CN", { style: "currency", currency, minimumFractionDigits: 2 }).format(cents / 100);
}

function formatTokens(entry: WalletEntry) {
  const total = entry.total_tokens ?? ((entry.prompt_tokens ?? 0) + (entry.completion_tokens ?? 0));
  return total ? `${total.toLocaleString("zh-CN")} tokens` : "-";
}

function entryTypeLabel(entry: WalletEntry) {
  return ({ recharge: "余额购买", topup: "余额购买", usage: "AI 消费", consume: "AI 消费", reward: "邀请奖励", aff_reward: "邀请奖励", refund: "退回余额", grant: "平台赠送" } as Record<string, string>)[entry.type || ""] || entry.type || (entry.direction === "debit" ? "AI 消费" : "余额入账");
}

function entryStatus(status?: string, direction?: string) {
  const tone = status === "succeeded" || status === "settled" ? "success" : status === "pending" ? "warning" : status === "failed" || status === "revoked" ? "danger" : "neutral";
  return <Badge tone={tone}>{status === "settled" && direction === "debit" ? "已结算" : statusLabel(status)}</Badge>;
}

function rechargeStatus(status: string) {
  return statusLabel(status);
}

function rechargeTone(status: string) {
  return status === "paid" || status === "succeeded" || status === "settled" ? "success" : status === "pending" || status === "created" ? "warning" : status === "failed" || status === "cancelled" ? "danger" : "neutral";
}

function statusLabel(status?: string) {
  return ({ pending: "待确认", created: "已创建", paid: "已支付", succeeded: "已到账", settled: "已到账", failed: "失败", cancelled: "已取消", revoked: "已撤销" } as Record<string, string>)[status || ""] || status || "已记录";
}
